import { SupplierUserRole } from '@choco-admin/types';
import { Button, Card, CardBody } from '@heroui/react';
import React, { useState, useEffect } from 'react';

import { AlertCard } from '../../../../components/AlertCard';
import { SupplierRoleSelect } from '../../../../components/SupplierRoleSelect';
import { type UserAccessNotice } from '../../../../utils/userEntityAccess';
import { useSsoRestriction } from '../../contexts';
import { useSupplierLinking } from '../../hooks';
import { SsoRestrictionTooltip } from '../SsoRestrictionTooltip';

import { ConfirmRemoveSupplierModal } from './ConfirmRemoveSupplierModal';
import { LinkSupplierModal } from './LinkSupplierModal';
import { SupplierSelectOption } from './utils';

interface SupplierLinkingFieldProps {
  userId: string;
  userName: string;
  initialLinkedSupplier?: SupplierSelectOption | null;
  initialRole?: SupplierUserRole | null;
  disabled?: boolean;
  /** Set when this user must not be linked to a distributor. */
  linkBlock?: UserAccessNotice | null;
  /** Off when a warning above already explains the block. */
  showLinkBlockMessage?: boolean;
  onLinkedSupplierChange?: (supplierId: string | null) => void;
}

export const SupplierLinkingField: React.FC<SupplierLinkingFieldProps> = ({
  userId,
  userName,
  initialLinkedSupplier = null,
  initialRole = null,
  disabled = false,
  linkBlock = null,
  showLinkBlockMessage = true,
  onLinkedSupplierChange,
}) => {
  const { isSsoUser } = useSsoRestriction();
  // The distributor link is managed by the identity provider for SSO users.
  const isDisabled = disabled || isSsoUser;
  const [linkedSupplier, setLinkedSupplier] =
    useState<SupplierSelectOption | null>(initialLinkedSupplier);
  const [role, setRole] = useState<SupplierUserRole | null>(initialRole);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const {
    unlinkUserFromSupplier,
    updateSupplierRole,
    isUnlinking,
    isUpdatingRole,
  } = useSupplierLinking();

  // Update local state when initial data changes
  useEffect(() => {
    setLinkedSupplier(initialLinkedSupplier);
  }, [initialLinkedSupplier]);

  useEffect(() => {
    setRole(initialRole);
  }, [initialRole]);

  const linkedSupplierId = linkedSupplier?.value || null;
  useEffect(() => {
    onLinkedSupplierChange?.(linkedSupplierId);
  }, [linkedSupplierId, onLinkedSupplierChange]);

  const handleRemoveSupplier = async () => {
    try {
      // The unlink mutation clears the user's supplier role atomically on the
      // backend — no separate role-removal call needed.
      await unlinkUserFromSupplier(userId);

      // Update local state
      setLinkedSupplier(null);
      setRole(null);
      setIsRemoveModalOpen(false);
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleModalSuccess = (selectedSupplier: SupplierSelectOption) => {
    // Update local state immediately
    setLinkedSupplier(selectedSupplier);
    // A freshly linked distributor has no role assigned yet.
    setRole(null);
    setIsModalOpen(false);
    // TODO: Implement actual supplier linking mutation
  };

  const handleRoleChange = async (newRole: SupplierUserRole) => {
    const previousRole = role;
    // Optimistically reflect the change; revert on failure.
    setRole(newRole);
    try {
      await updateSupplierRole(userId, newRole);
    } catch (error) {
      setRole(previousRole);
      // Error notification is handled in the hook.
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-foreground">
              Linked Distributor
            </h3>
            <SsoRestrictionTooltip />
            {linkedSupplier && (
              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-success-700 bg-success-100 rounded-full">
                1
              </span>
            )}
          </div>
          <p className="text-sm text-default-500 mt-1">
            Link this user to a distributor to give them distributor-specific
            access.
          </p>
        </div>
        {!linkedSupplier && (
          <Button
            color="primary"
            variant="flat"
            size="sm"
            onPress={() => setIsModalOpen(true)}
            isDisabled={isDisabled || !!linkBlock}
          >
            + Link Distributor
          </Button>
        )}
      </div>

      {linkBlock && showLinkBlockMessage && (
        <AlertCard
          type="warning"
          title={linkBlock.title}
          message={linkBlock.message}
        />
      )}

      {linkedSupplier ? (
        <Card className="shadow-sm">
          <CardBody className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-default-900 truncate">
                  {linkedSupplier.label}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <SupplierRoleSelect
                  value={role}
                  onChange={handleRoleChange}
                  disabled={isDisabled || isUpdatingRole}
                />
                <Button
                  color="primary"
                  variant="light"
                  size="sm"
                  onPress={() => setIsModalOpen(true)}
                  isDisabled={isDisabled || isUnlinking || !!linkBlock}
                >
                  Change
                </Button>
                <Button
                  color="danger"
                  variant="light"
                  size="sm"
                  onPress={() => setIsRemoveModalOpen(true)}
                  isDisabled={isDisabled || isUnlinking}
                  isLoading={isUnlinking}
                >
                  Remove
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="text-center py-6 bg-default-50 rounded-lg border-2 border-dashed border-default-200">
          <p className="text-default-500 mb-2">
            This user is not linked to any distributor yet.
          </p>
          <p className="text-sm text-default-400">
            Click "Link Distributor" to search and link this user to a
            distributor.
          </p>
        </div>
      )}

      <LinkSupplierModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
        userName={userName}
        onSuccess={handleModalSuccess}
      />

      <ConfirmRemoveSupplierModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={handleRemoveSupplier}
        userName={userName}
        supplierName={linkedSupplier?.label || 'this distributor'}
        isRemoving={isUnlinking}
      />
    </div>
  );
};
